// The state machine behind the ember roster's offscreen speech lane. Kept free of PIXI (like
// faction_tracker_geometry.ts) so the rules can be unit-tested without a renderer; ember_roster.ts
// owns the drawing and just asks this what to show.
//
// THE CLUTTER RULE. A camp member who speaks while the player can't see them gets their line shown
// over their roster card. A readable bubble is several times wider than the ~56px card it belongs
// to, so two of them side by side would overlap into mush — the lane therefore holds exactly ONE at
// a time and everyone else queues:
//
//   - a line holds the lane for MIN_MS before it can be pushed out, so it can actually be read,
//   - and gives it up at MAX_MS even if nobody is waiting, so the HUD returns to quiet,
//   - a burst keeps only the newest QUEUE_MAX lines: when several people shout at once, current
//     lines are worth more than stale ones,
//   - and every speaker's card flashes the moment they speak, so a queued line is acknowledged
//     immediately even though its bubble has to wait its turn.

export interface LaneLine {
    entityId: string
    name: string
    text: string
}

export interface LaneMember {
    entityId: string
    name: string
}

export const MIN_MS = 2200
export const MAX_MS = 5200
export const QUEUE_MAX = 3
export const PULSE_MS = 700

export class RosterSpeechLane {
    private queue: LaneLine[] = []
    private current: LaneLine | null = null
    private elapsedMs = 0
    // The last line already reacted to per member, so one utterance raises one bubble rather than
    // one per frame it remains inside its ~3s window.
    private handled: Map<string, string> = new Map()
    // entityId -> remaining flash time.
    private pulses: Map<string, number> = new Map()

    // Advance one frame. `getSpeech` must answer null for anyone the player can currently SEE —
    // their bubble is already drawn over their head in the world, and repeating it here is noise.
    update = (dtMs: number, members: LaneMember[], getSpeech: (entityId: string) => string | null) => {
        this.collect(members, getSpeech)
        this.advance(dtMs, getSpeech)
        this.advancePulses(dtMs)
    }

    // The line to show, or null for an empty lane.
    currentLine = (): LaneLine | null => this.current

    // 0..1 flash strength for a member's card this frame.
    pulseAlpha = (entityId: string): number => (this.pulses.get(entityId) || 0) / PULSE_MS

    queueLength = (): number => this.queue.length

    // A member who left the roster keeps neither the lane nor a place in the queue.
    forget = (entityId: string) => {
        this.queue = this.queue.filter(q => q.entityId !== entityId)
        this.handled.delete(entityId)
        this.pulses.delete(entityId)
        if (this.current?.entityId === entityId) {
            this.clear()
        }
    }

    private collect = (members: LaneMember[], getSpeech: (entityId: string) => string | null) => {
        members.forEach(member => {
            const line = getSpeech(member.entityId)
            if (!line) {
                // Window lapsed, or they walked into view: re-arm so saying the same thing again
                // later counts as a fresh utterance.
                this.handled.delete(member.entityId)
                return
            }
            if (this.handled.get(member.entityId) === line) {
                return
            }
            this.handled.set(member.entityId, line)
            this.pulses.set(member.entityId, PULSE_MS)
            this.queue.push({ entityId: member.entityId, name: member.name, text: line })
            if (this.queue.length > QUEUE_MAX) {
                this.queue.splice(0, this.queue.length - QUEUE_MAX)
            }
        })
    }

    private advance = (dtMs: number, getSpeech: (entityId: string) => string | null) => {
        if (this.current) {
            this.elapsedMs += dtMs
            const readable = this.elapsedMs >= MIN_MS
            // Let it go early once the player has caught up with the speaker — they can see the real
            // bubble now — but never before it has been up long enough to read.
            const caughtUp = getSpeech(this.current.entityId) === null
            if ((readable && (caughtUp || this.queue.length > 0)) || this.elapsedMs >= MAX_MS) {
                this.clear()
            }
        }

        if (!this.current && this.queue.length > 0) {
            this.current = this.queue.shift()!
            this.elapsedMs = 0
        }
    }

    private advancePulses = (dtMs: number) => {
        for (const [entityId, remaining] of Array.from(this.pulses.entries())) {
            const next = remaining - dtMs
            if (next <= 0) {
                this.pulses.delete(entityId)
            } else {
                this.pulses.set(entityId, next)
            }
        }
    }

    private clear = () => {
        this.current = null
        this.elapsedMs = 0
    }
}
